import { useCallback, useState } from 'react'

export const ACTIVITY_SESSION_PREFIX = 'activity_session_'

export interface ActivitySession<TConfig, TGameState = never> {
  slug:       string
  config:     TConfig
  gameState?: TGameState
  startedAt:  number
}

/** Scan localStorage for any saved activity session. Used by Dashboard. */
export function findAnySavedActivity(): ActivitySession<unknown> | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(ACTIVITY_SESSION_PREFIX)) {
        const raw = localStorage.getItem(key)
        if (raw) return JSON.parse(raw) as ActivitySession<unknown>
      }
    }
  } catch {
    // localStorage may be disabled or unavailable
  }
  return null
}

export function clearActivitySession(slug: string): void {
  try {
    localStorage.removeItem(`${ACTIVITY_SESSION_PREFIX}${slug}`)
  } catch {
    // ignore
  }
}

function readSession<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : null
  } catch {
    // malformed JSON or localStorage unavailable
    return null
  }
}

/**
 * Per-activity session storage (localStorage — persists across browser sessions).
 *
 * - `savedSession`: snapshot from mount (null if none)
 * - `saveConfig`:    call when user starts (setup → playing); preserves startedAt
 * - `saveGameState`: call to attach mid-game state (future use)
 * - `clear`:         call on game complete or user goes back to setup
 *
 * Pass `enabled = false` (e.g. when in session mode) to disable all I/O.
 */
export function useActivitySession<TConfig, TGameState = never>(
  slug: string,
  enabled = true,
) {
  const key = `${ACTIVITY_SESSION_PREFIX}${slug}`

  // Lazy initializer — reads localStorage once synchronously before first render.
  const [savedSession] = useState<ActivitySession<TConfig, TGameState> | null>(() =>
    enabled ? readSession<ActivitySession<TConfig, TGameState>>(key) : null
  )

  const saveConfig = useCallback(
    (config: TConfig) => {
      if (!enabled) return
      // Preserve startedAt if a session already exists in storage.
      const existing = readSession<ActivitySession<TConfig, TGameState>>(key)
      const session: ActivitySession<TConfig, TGameState> = {
        slug,
        config,
        startedAt: existing?.startedAt ?? Date.now(),
      }
      try {
        localStorage.setItem(key, JSON.stringify(session))
      } catch {
        // quota exceeded or storage unavailable
      }
    },
    [key, slug, enabled],
  )

  const saveGameState = useCallback(
    (gameState: TGameState) => {
      if (!enabled) return
      const existing = readSession<ActivitySession<TConfig, TGameState>>(key)
      if (!existing) return
      try {
        localStorage.setItem(key, JSON.stringify({ ...existing, gameState }))
      } catch {
        // ignore
      }
    },
    [key, enabled],
  )

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore
    }
  }, [key])

  return { savedSession, saveConfig, saveGameState, clear }
}
